import {
  type FC,
  useEffect,
} from 'react';

import AppLayout from '@/components/feature/AppLayout';

import {
  useToast,
} from '@/components/base';

import {
  useGoogleDriveIntegration,
} from '@/hooks/useGoogleDriveIntegration';

import {
  useGoogleCalendarIntegration,
} from '@/hooks/useGoogleCalendarIntegration';

import {
  useGmailIntegration,
} from '@/hooks/useGmailIntegration';

import {
  useWhatsAppIntegration,
} from '@/hooks/useWhatsAppIntegration';

import {
  LocalAIIntegrationCard,
  SignatureIntegrationCard,
  SystemHealthCard,
} from '@/components/feature/integrations/RuntimeIntegrationCards';

import IntegrationConfigurationCenter from '@/components/feature/integrations/IntegrationConfigurationCenter';

const futureIntegrations = [
  {
    name:
      'Gmail',

    icon:
      'ri-mail-line',

    desc:
      'Importação de publicações e comunicações.',
  },

  {
    name:
      'Google Calendar',

    icon:
      'ri-calendar-line',

    desc:
      'Sincronização futura de audiências e compromissos.',
  },

  {
    name:
      'Asaas',

    icon:
      'ri-bank-line',

    desc:
      'Cobranças e sincronização financeira.',
  },

  {
    name:
      'WhatsApp',

    icon:
      'ri-whatsapp-line',

    desc:
      'Mensageria e automações futuras.',
  },

  {
    name:
      'IA',

    icon:
      'ri-brain-line',

    desc:
      'Provider de inteligência artificial desacoplado.',
  },
];

const IntegrationsPage:
  FC = () => {
  const {
    addToast,
  } =
    useToast();

  const {
    googleDrive,

    isLoadingGoogleDrive,
    isDisconnectingGoogleDrive,

    loadGoogleDriveStatus,
    connectGoogleDrive,
    disconnectGoogleDrive,
  } =
    useGoogleDriveIntegration();

  const {
    googleCalendar,
    isLoadingGoogleCalendar,
    isSyncingGoogleCalendar,
    isDisconnectingGoogleCalendar,
    loadGoogleCalendarStatus,
    connectGoogleCalendar,
    syncGoogleCalendar,
    disconnectGoogleCalendar,
  } =
    useGoogleCalendarIntegration();

  const {
    gmail,
    isLoadingGmail,
    isSyncingGmail,
    isDisconnectingGmail,
    loadGmailStatus,
    connectGmail,
    syncGmail,
    disconnectGmail,
  } =
    useGmailIntegration();

  const {
    whatsapp,
    isLoadingWhatsApp,
  } =
    useWhatsAppIntegration();

  useEffect(
    () => {
      const url =
        new URL(
          window.location.href
        );

      const result =
        url.searchParams.get(
          'googleDrive'
        );

      const reason =
        url.searchParams.get(
          'reason'
        );

      if (
        result ===
        'connected'
      ) {
        addToast(
          'Google Drive conectado com sucesso.',
          'success'
        );

        void loadGoogleDriveStatus();
      }

      if (
        result ===
        'error'
      ) {
        const messages:
          Record<
            string,
            string
          > = {
          access_denied:
            'A autorização do Google Drive foi cancelada.',

          invalid_state:
            'A validação de segurança da autorização expirou ou é inválida.',

          missing_refresh_token:
            'O Google não forneceu um refresh token. Tente conectar novamente.',

          token_exchange_failed:
            'Não foi possível concluir a autorização com o Google.',

          server_not_configured:
            'A integração Google ainda não está configurada no servidor.',

          authentication_required:
            'Sua sessão expirou durante a autorização.',

          missing_code:
            'O Google não retornou o código de autorização.',
        };

        addToast(
          messages[
            reason ??
            ''
          ] ||
          'Não foi possível conectar o Google Drive.',
          'error'
        );
      }

      if (result) {
        url.searchParams.delete(
          'googleDrive'
        );

        url.searchParams.delete(
          'reason'
        );

        window.history.replaceState(
          {},
          '',
          url.toString()
        );
      }
    },
    [
      addToast,
      loadGoogleDriveStatus,
    ]
  );

  useEffect(
    () => {
      const url =
        new URL(
          window.location.href
        );

      const result =
        url.searchParams.get(
          'googleCalendar'
        );

      const reason =
        url.searchParams.get(
          'reason'
        );

      if (result === 'connected') {
        addToast(
          'Google Calendar conectado com sucesso.',
          'success'
        );

        void loadGoogleCalendarStatus();
      }

      if (result === 'error') {
        const messages:
          Record<string, string> = {
          access_denied:
            'A autorização do Google Calendar foi cancelada.',

          invalid_state:
            'A validação de segurança da autorização expirou ou é inválida.',

          missing_refresh_token:
            'O Google não forneceu um refresh token. Tente conectar novamente.',

          token_exchange_failed:
            'Não foi possível concluir a autorização do Google Calendar.',

          server_not_configured:
            'A integração Google ainda não está configurada no servidor.',

          authentication_required:
            'Sua sessão expirou durante a autorização.',

          missing_code:
            'O Google não retornou o código de autorização.',
        };

        addToast(
          messages[reason ?? ''] ||
            'Não foi possível conectar o Google Calendar.',
          'error'
        );
      }

      if (result) {
        url.searchParams.delete(
          'googleCalendar'
        );

        url.searchParams.delete(
          'reason'
        );

        window.history.replaceState(
          {},
          '',
          url.toString()
        );
      }
    },
    [
      addToast,
      loadGoogleCalendarStatus,
    ]
  );

  useEffect(
    () => {
      const url = new URL(window.location.href);
      const result = url.searchParams.get('gmail');
      const reason = url.searchParams.get('reason');

      if (result === 'connected') {
        addToast('Gmail conectado com sucesso.', 'success');
        void loadGmailStatus();
      }

      if (result === 'error') {
        const messages: Record<string, string> = {
          access_denied: 'A autorização do Gmail foi cancelada.',
          invalid_state: 'A validação de segurança da autorização expirou ou é inválida.',
          missing_refresh_token: 'O Google não forneceu refresh token para o Gmail. Tente conectar novamente.',
          token_exchange_failed: 'Não foi possível concluir a autorização do Gmail.',
          account_email_missing: 'Não foi possível identificar a conta Gmail conectada.',
          server_not_configured: 'A integração Google ainda não está configurada no servidor.',
          authentication_required: 'Sua sessão expirou durante a autorização.',
          missing_code: 'O Google não retornou o código de autorização.',
        };
        addToast(messages[reason ?? ''] || 'Não foi possível conectar o Gmail.', 'error');
      }

      if (result) {
        url.searchParams.delete('gmail');
        url.searchParams.delete('reason');
        window.history.replaceState({}, '', url.toString());
      }
    },
    [addToast, loadGmailStatus]
  );

  const driveStatus =
    isLoadingGoogleDrive
      ? 'Verificando...'
      : googleDrive?.connected
        ? 'Conectado'
        : googleDrive?.configured
          ? 'Não conectado'
          : 'Configuração necessária';

  const driveStatusClass =
    googleDrive?.connected
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : googleDrive?.configured
        ? 'bg-gray-100 text-gray-500'
        : 'bg-amber-50 text-amber-700 border border-amber-200';

  const calendarStatus =
    isLoadingGoogleCalendar
      ? 'Verificando...'
      : googleCalendar?.connected
        ? 'Conectado'
        : googleCalendar?.configured
          ? 'Não conectado'
          : 'Configuração necessária';

  const calendarStatusClass =
    googleCalendar?.connected
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : googleCalendar?.configured
        ? 'bg-gray-100 text-gray-500'
        : 'bg-amber-50 text-amber-700 border border-amber-200';

  const gmailStatus =
    isLoadingGmail
      ? 'Verificando...'
      : gmail?.connected
        ? gmail.importEnabled
          ? 'Envio + importação'
          : 'Somente envio'
        : gmail?.configured
          ? 'Não conectado'
          : 'Configuração necessária';

  const gmailStatusClass =
    gmail?.connected
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : gmail?.configured
        ? 'bg-gray-100 text-gray-500'
        : 'bg-amber-50 text-amber-700 border border-amber-200';

  const whatsappStatus =
    isLoadingWhatsApp
      ? 'Verificando...'
      : whatsapp?.fullyConfigured
        ? 'Configurado'
        : whatsapp?.sendConfigured ||
            whatsapp?.webhookConfigured
          ? 'Configuração parcial'
          : 'Configuração necessária';

  const whatsappStatusClass =
    whatsapp?.fullyConfigured
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : whatsapp?.sendConfigured ||
          whatsapp?.webhookConfigured
        ? 'bg-amber-50 text-amber-700 border border-amber-200'
        : 'bg-gray-100 text-gray-500';

  const handleGmailDisconnect = async () => {
    try {
      await disconnectGmail();
      addToast('Gmail desconectado.', 'success');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Não foi possível desconectar o Gmail.', 'error');
    }
  };

  const handleGmailSync = async () => {
    try {
      await syncGmail();
      addToast('Gmail sincronizado.', 'success');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Não foi possível sincronizar o Gmail.', 'error');
    }
  };

  const handleCalendarDisconnect =
    async () => {
      try {
        await disconnectGoogleCalendar();

        addToast(
          'Google Calendar desconectado.',
          'success'
        );
      } catch (error) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível desconectar o Google Calendar.',
          'error'
        );
      }
    };

  const handleCalendarSync =
    async () => {
      try {
        await syncGoogleCalendar();

        addToast(
          'Google Calendar sincronizado.',
          'success'
        );
      } catch (error) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível sincronizar o Google Calendar.',
          'error'
        );
      }
    };

  const handleDisconnect =
    async () => {
      try {
        await disconnectGoogleDrive();

        addToast(
          'Google Drive desconectado.',
          'success'
        );
      } catch (error) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível desconectar o Google Drive.',
          'error'
        );
      }
    };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-foreground-900">
            Integrações
          </h2>

          <p className="text-sm text-foreground-500 mt-0.5">
            Conecte serviços externos ao BM Legal OS sem acoplar o domínio a fornecedores específicos.
          </p>
        </div>

        <IntegrationConfigurationCenter />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SystemHealthCard />
          <LocalAIIntegrationCard />
          <SignatureIntegrationCard />

          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-hard-drive-line text-xl text-foreground-600"></i>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground-900">
                  Google Drive
                </h4>

                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${driveStatusClass}`}
                >
                  {driveStatus}
                </span>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-3">
              Armazenamento externo dos arquivos do escritório. O InActis manterá apenas referências e metadados.
            </p>

            {googleDrive?.connected && (
              <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5 text-[11px] text-emerald-800">
                <div>
                  Escopo: arquivos utilizados pelo InActis
                </div>

                {googleDrive.connectedAt && (
                  <div className="mt-1">
                    Conectado em{' '}
                    {new Date(
                      googleDrive.connectedAt
                    ).toLocaleString(
                      'pt-BR'
                    )}
                  </div>
                )}
              </div>
            )}

            {!googleDrive?.configured && !isLoadingGoogleDrive && (
              <div className="mb-3 rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                Credenciais OAuth ainda não configuradas no servidor.
              </div>
            )}

            {googleDrive?.connected ? (
              <button
                type="button"
                onClick={() =>
                  void handleDisconnect()
                }
                disabled={
                  isDisconnectingGoogleDrive
                }
                className="w-full py-2 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                {isDisconnectingGoogleDrive
                  ? 'Desconectando...'
                  : 'Desconectar'}
              </button>
            ) : (
              <button
                type="button"
                onClick={
                  connectGoogleDrive
                }
                disabled={
                  isLoadingGoogleDrive ||
                  !googleDrive?.configured
                }
                className="w-full py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {googleDrive?.configured
                  ? 'Conectar Google Drive'
                  : 'Configuração necessária'}
              </button>
            )}
          </div>

          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-calendar-line text-xl text-foreground-600"></i>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground-900">
                  Google Calendar
                </h4>

                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${calendarStatusClass}`}>
                  {calendarStatus}
                </span>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-3">
              Sincroniza somente eventos gerenciados pelo BM Legal. A agenda pessoal inteira não é importada.
            </p>

            {googleCalendar?.connected && (
              <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5 text-[11px] text-emerald-800 space-y-1">
                <div>
                  {googleCalendar.linkedEvents} evento(s) vinculado(s) · {googleCalendar.conflicts} conflito(s)
                </div>

                {googleCalendar.lastSyncAt && (
                  <div>
                    Última sincronização: {new Date(googleCalendar.lastSyncAt).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            )}

            {!googleCalendar?.configured && !isLoadingGoogleCalendar && (
              <div className="mb-3 rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                OAuth Google ainda não configurado no servidor.
              </div>
            )}

            {googleCalendar?.connected ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => void handleCalendarSync()}
                  disabled={isSyncingGoogleCalendar}
                  className="py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {isSyncingGoogleCalendar ? 'Sincronizando...' : 'Sincronizar'}
                </button>

                <button
                  type="button"
                  onClick={() => void handleCalendarDisconnect()}
                  disabled={isDisconnectingGoogleCalendar}
                  className="py-2 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  {isDisconnectingGoogleCalendar ? 'Desconectando...' : 'Desconectar'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={connectGoogleCalendar}
                disabled={
                  isLoadingGoogleCalendar ||
                  !googleCalendar?.configured
                }
                className="w-full py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {googleCalendar?.configured
                  ? 'Conectar Google Calendar'
                  : 'Configuração necessária'}
              </button>
            )}
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-mail-line text-xl text-foreground-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900">Gmail</h4>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${gmailStatusClass}`}>
                  {gmailStatus}
                </span>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-3">
              Envio real pelo Gmail. A importação é opcional e armazena apenas metadados, assunto e snippet de mensagens vinculáveis.
            </p>

            {gmail?.connected && (
              <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5 text-[11px] text-emerald-800 space-y-1">
                {gmail.accountEmail && <div>Conta: {gmail.accountEmail}</div>}
                <div>{gmail.outboundCommunications} enviada(s) · {gmail.inboundCommunications} recebida(s) relevante(s)</div>
                <div>{gmail.importEnabled ? 'Importação de metadados ativa' : 'Caixa postal não é lida'}</div>
                {gmail.lastSyncAt && <div>Última sincronização: {new Date(gmail.lastSyncAt).toLocaleString('pt-BR')}</div>}
              </div>
            )}

            {!gmail?.configured && !isLoadingGmail && (
              <div className="mb-3 rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                OAuth Google ainda não configurado no servidor.
              </div>
            )}

            {gmail?.connected ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {gmail.importEnabled ? (
                    <button type="button" onClick={() => void handleGmailSync()} disabled={isSyncingGmail} className="py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60">
                      {isSyncingGmail ? 'Sincronizando...' : 'Sincronizar'}
                    </button>
                  ) : (
                    <button type="button" onClick={() => connectGmail('full')} className="py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
                      Ativar importação
                    </button>
                  )}
                  <button type="button" onClick={() => void handleGmailDisconnect()} disabled={isDisconnectingGmail} className="py-2 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60">
                    {isDisconnectingGmail ? 'Desconectando...' : 'Desconectar'}
                  </button>
                </div>
                {!gmail.importEnabled && (
                  <p className="text-[10px] text-amber-700">A leitura de metadados usa escopo restrito do Google e deve ser ativada conscientemente.</p>
                )}
              </div>
            ) : (
              <button type="button" onClick={() => connectGmail('send')} disabled={isLoadingGmail || !gmail?.configured} className="w-full py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {gmail?.configured ? 'Conectar Gmail para envio' : 'Configuração necessária'}
              </button>
            )}
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl text-foreground-600"></i>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground-900">
                  WhatsApp Cloud API
                </h4>

                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${whatsappStatusClass}`}>
                  {whatsappStatus}
                </span>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-3">
              Integração oficial Meta para envio e recebimento. Não usa WhatsApp Web, scraping ou biblioteca não oficial.
            </p>

            {whatsapp && (
              <div className="mb-3 rounded-lg border border-background-200 bg-white p-2.5 text-[11px] text-foreground-600 space-y-1">
                <div>
                  Envio: {whatsapp.sendConfigured ? 'configurado' : 'pendente'}
                </div>

                <div>
                  Webhook: {whatsapp.webhookConfigured ? 'configurado' : 'pendente'}
                </div>

                <div>
                  {whatsapp.outboundCommunications} enviada(s) · {whatsapp.inboundCommunications} recebida(s)
                </div>

                {whatsapp.attentionCommunications > 0 && (
                  <div className="text-amber-700">
                    {whatsapp.attentionCommunications} comunicação(ões) exigindo atenção
                  </div>
                )}

                {whatsapp.graphApiVersion && (
                  <div>
                    Graph API: {whatsapp.graphApiVersion}
                  </div>
                )}
              </div>
            )}

            {!whatsapp?.fullyConfigured && !isLoadingWhatsApp && (
              <div className="rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-[11px] text-amber-800">
                A configuração é feita por secrets do Cloudflare e pelo Meta Business. Nenhum token é armazenado no D1.
              </div>
            )}
          </div>
          {futureIntegrations.filter((integration) => integration.name !== 'IA' && integration.name !== 'Google Calendar' && integration.name !== 'Gmail' && integration.name !== 'WhatsApp').map(
            (
              integration
            ) => (
              <div
                key={
                  integration.name
                }
                className="bg-background-50 border border-background-200 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                    <i
                      className={`${integration.icon} text-xl text-foreground-600`}
                    ></i>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">
                      {integration.name}
                    </h4>

                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
                      Futuro
                    </span>
                  </div>
                </div>

                <p className="text-xs text-foreground-500 mb-4">
                  {integration.desc}
                </p>

                <button
                  type="button"
                  disabled
                  className="w-full py-2 rounded-lg text-sm font-medium bg-background-100 text-foreground-400 cursor-not-allowed"
                >
                  Indisponível
                </button>
              </div>
            )
          )}
        </div>

        <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
          <p className="text-xs text-blue-700 font-medium">
            <i className="ri-information-line mr-1"></i>
            Google Drive possui OAuth real e operacoes de pasta/upload. Os arquivos permanecem no Drive; o BM Legal conserva somente vinculos e metadados.
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default IntegrationsPage;
